import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router";

/**
 * Phase 13.5 (IA implementation) — footer-anchored settings tray.
 *
 * The IA review (DECISIONS.md 2026-05-09 IA review block) collapsed AI +
 * Integrations + Alerts out of the primary sidebar into a secondary tray
 * that hangs off the IdentityFooter. Items here are operational /
 * configuration concerns — pages users visit infrequently, not their
 * day-to-day surfaces. Email admin (NEW in 13.5) lives here too.
 *
 * UX:
 *   - "Settings" button in the IdentityFooter toggles the tray.
 *   - Tray opens UPWARD as an absolute-positioned panel anchored to the
 *     trigger button (sidebar is narrow + scrollable so a popover is
 *     more honest than an inline expander that pushes the toggle row).
 *   - Click outside or Escape closes.
 *   - Active link highlight matches the primary sidebar's exact-pathname
 *     comparison so only one item is active at a time.
 */

interface TrayItem {
  readonly label: string;
  readonly to: string;
  readonly icon?: ReactNode;
  readonly comingPhase?: string;
}

const TRAY_ITEMS: readonly TrayItem[] = [
  { label: "Household profile", to: "/settings/household" },
  { label: "Email admin", to: "/settings/emails" },
  { label: "AI settings", to: "/settings/ai" },
  { label: "Integrations", to: "/integrations" },
  { label: "Staging inbox", to: "/integrations/staging" },
  { label: "Alerts", to: "/alerts" },
];

export function AppShellSettingsTray() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="w-full rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-center text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
      >
        Settings
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Settings menu"
          className="absolute bottom-full left-0 right-0 z-50 mb-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-surface)] p-1 shadow-lg"
        >
          {TRAY_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  "block rounded-md px-3 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent)]"
                    : "text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
                ].join(" ")
              }
              role="menuitem"
              end
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
